'use client';

import {
	atomWithStorage,
	DateObject,
	EventStore,
	isAuthenticatedAtom,
	notificationApi,
	NotificationService,
	type PostUserDeviceTokenMePayload,
	useIntl,
	useLogger,
	useSettings,
	useSubscribe
} from '@capsicum/common';
import { deleteToken, isSupported as getIsSupported, getMessaging, getToken, type Messaging } from 'firebase/messaging';
import { useAtom, useAtomValue } from 'jotai';
import { RESET } from 'jotai/utils';
import { type PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useFirebaseInitializer } from '../../hooks/firebase/useFirebaseInitializer';
import { ModalService } from '../../services/alerts';
import { Notification as NotificationComponent } from '../../ui/molecules/notifications/Notification';
import { env } from '../../util';

import { useWebPushDeviceInfo } from './useWebPushDeviceInfo';
import { actionNextSchedulePermissionRequestAtom, initialSchedulePermissionRequestAtom } from './webPush.atom';
import {
	PermissionStatusEnum,
	type RequestPermissionFn,
	WebPushContext,
	type WebPushContextShape
} from './WebPushContext';
import { WebPushNotification } from './WebPushNotification';

export const webPushNotificationDebugEnabledAtom = atomWithStorage('webPushNotificationDebugEnabled', false);

export type WebPushWrapperProps = PropsWithChildren<{
	deviceType: PostUserDeviceTokenMePayload['deviceType'];
}>;

export const WebPushProvider = ({ children, deviceType }: WebPushWrapperProps) => {
	const {
		areSettingsLoading,
		settings: {
			isPushNotificationsEnabled,
			pushNotificationsActionsPromptMinutesDelay,
			pushNotificationsPromptMinutesDelay
		}
	} = useSettings();
	const initializeFirebase = useFirebaseInitializer();
	const { deviceId, deviceName } = useWebPushDeviceInfo();
	const currentModalId = useRef<null | string>(null);
	const [initialSchedule, setInitialSchedule] = useAtom(initialSchedulePermissionRequestAtom);
	const [actionNextSchedule, setActionNextSchedule] = useAtom(actionNextSchedulePermissionRequestAtom);
	const [permission, setPermission] = useState<NotificationPermission>(
		typeof window !== 'undefined' && 'Notification' in window
			? Notification.permission
			: PermissionStatusEnum.DENIED
	);
	const [isSupported, setIsSupported] = useState(false);
	const authentication = useAtomValue(isAuthenticatedAtom);
	const isAuthenticated = authentication.state === 'hasData' ? authentication.data : false;
	const messaging = useRef<Messaging>();
	const { $t } = useIntl();

	const [swRegistration, setSwRegistration] = useState<ServiceWorkerRegistration>();

	const webPushNotificationDebugEnabled = useAtomValue(webPushNotificationDebugEnabledAtom);

	const logger = useLogger();

	useEffect(() => {
		/* Check if user changed notifications from default browser. Not supported in Safari.
			For more info check https://developer.mozilla.org/en-US/docs/Web/API/Permissions_API
		*/
		isPushNotificationsEnabled &&
			navigator?.permissions?.query &&
			navigator.permissions.query({ name: 'notifications' }).then(permissionStatus => {
				permissionStatus.onchange = () => {
					const status: NotificationPermission =
						permissionStatus.state === 'prompt' ? PermissionStatusEnum.DEFAULT : permissionStatus.state;

					setPermission(status);
				};
			});
	}, [isPushNotificationsEnabled]);

	const sendNotification = useCallback(
		(userPermission: NotificationPermission) => {
			isPushNotificationsEnabled &&
				NotificationService.addNotification({
					component: (
						<NotificationComponent
							message={$t(
								`pushNotifications.notification.${userPermission === PermissionStatusEnum.GRANTED ? 'enabled' : 'disabled'}`
							)}
							variant="success"
						/>
					)
				});
		},
		[isPushNotificationsEnabled]
	);

	const log = useCallback(
		(level: 'info' | 'warn', message: string, data?: any) => {
			if (!webPushNotificationDebugEnabled || !isPushNotificationsEnabled) {
				return;
			}

			logger[level](`(Firebase Web Push) - ${message}`, data);
		},
		[webPushNotificationDebugEnabled, logger, isPushNotificationsEnabled]
	);

	const sendDeviceInfoToServer = useCallback(
		async (deviceToken: string) => {
			if (!deviceId || !isPushNotificationsEnabled) return;
			const dataToSend: PostUserDeviceTokenMePayload = {
				deviceId,
				deviceName,
				deviceToken,
				deviceType
			};
			const { postUserDeviceTokenMe } = await notificationApi;

			await postUserDeviceTokenMe(dataToSend);
			log('info', 'Device Info', dataToSend);
		},
		[deviceId, deviceName, deviceType, log, isPushNotificationsEnabled]
	);

	const setupFirebase = useCallback(async () => {
		if (!isPushNotificationsEnabled) return;
		log('info', 'Initializing...');

		if (messaging.current) {
			log('info', 'Messaging already initialized');

			return;
		}

		try {
			const app = initializeFirebase();
			const _messaging = getMessaging(app);

			messaging.current = _messaging;

			const token = await getToken(_messaging, {
				serviceWorkerRegistration: swRegistration,
				vapidKey: env.NEXT_PUBLIC_FIREBASE_VAPID_KEY
			}).catch(() => {
				throw new Error('Could not retrieve token');
			});

			await sendDeviceInfoToServer(token).catch(() => {
				throw new Error('Could not send device info to server');
			});

			log('info', 'Initialized!', token);
		} catch (e) {
			log('warn', 'Initialization failed', e);
		}
	}, [log, env, swRegistration, initializeFirebase, sendDeviceInfoToServer, isPushNotificationsEnabled]);

	const requestNotificationsPermission = useCallback(async () => {
		if (!isPushNotificationsEnabled) return;
		const userPermission = await Notification.requestPermission();

		if (userPermission === PermissionStatusEnum.DEFAULT) return;
		setPermission(userPermission);
		sendNotification(userPermission);
	}, []);

	const checkNotificationsStatus: RequestPermissionFn = useCallback(
		async props => {
			if (!isSupported) return log('info', 'Push Notifications not supported');
			if (currentModalId.current || !isPushNotificationsEnabled) return;
			// value of permission can be 'granted', 'default', 'denied'
			// granted: user has accepted the request
			// default: user has dismissed the notification permission popup by clicking on x
			// denied: user has denied the request.
			if (Notification.permission === PermissionStatusEnum.DEFAULT) {
				log('info', 'Notification permission not granted, action required. Showing modal...');
				currentModalId.current = ModalService.addModal({
					onDismiss: props?.onDismiss,
					renderComponent: ({ close }) => (
						<WebPushNotification
							caption={$t('pushNotifications.prompt.caption')}
							secondaryActionButtonText={$t('pushNotifications.prompt.secondaryButtonText')}
							testID="request-permission-drawer"
							onButtonPress={() => {
								requestNotificationsPermission();
								close();
							}}
							onSecondaryActionOnButtonPress={close}
							{...props}
						/>
					)
				});
			} else if (Notification.permission === PermissionStatusEnum.DENIED) {
				sendNotification(PermissionStatusEnum.DENIED);
				log('info', ' Permission denied!');
			} else {
				log('info', 'Notification permission granted !');
			}
		},
		[log, $t, isSupported, requestNotificationsPermission, sendNotification, isPushNotificationsEnabled]
	);

	const displayEnablePushNotificationsBySettingsModal = useCallback(() => {
		if (!isPushNotificationsEnabled) return;
		currentModalId.current = ModalService.addModal({
			renderComponent: ({ close }) => (
				<WebPushNotification
					buttonText={$t('pushNotifications.disclaimer.buttonText')}
					description={$t('pushNotifications.disclaimer.description')}
					testID="disclaimer-push-notifications-drawer"
					title={$t('pushNotifications.disclaimer.title')}
					onButtonPress={close}
				/>
			)
		});
	}, [isPushNotificationsEnabled]);

	const registerServiceWorker = useCallback(async () => {
		if (!isPushNotificationsEnabled || !navigator.serviceWorker?.register) return;
		setIsSupported(await getIsSupported());

		try {
			const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
				scope: 'firebase-cloud-messaging-push-scope',
				updateViaCache: 'imports'
			});

			registration.update();
			setSwRegistration(registration);
		} catch (e) {
			log('warn', 'Service worker registration failed', e);
		}
	}, [log, setIsSupported, isPushNotificationsEnabled]);

	const unRegisterMessaging = useCallback(() => {
		if (!messaging.current || !isPushNotificationsEnabled) return;
		deleteToken(messaging.current);
		messaging.current = undefined;
		log('info', 'Unregistered messaging');
	}, [log, isPushNotificationsEnabled]);

	const requestInitialPermission: RequestPermissionFn = useCallback(
		props => {
			if (initialSchedule === null || initialSchedule === 'never' || !isPushNotificationsEnabled) return;
			const now = new DateObject().unix();

			const onDismiss = () => {
				setInitialSchedule('never');
				currentModalId.current = null;
			};

			if (initialSchedule < now) {
				checkNotificationsStatus({ ...props, onDismiss });
			}
		},
		[initialSchedule, setInitialSchedule, checkNotificationsStatus, isPushNotificationsEnabled]
	);
	const requestPermissionAfterAction: RequestPermissionFn = useCallback(
		props => {
			if (actionNextSchedule === 'never' || !actionNextSchedule || !isPushNotificationsEnabled) return;

			const now = new DateObject().unix();

			const onDismiss = () => {
				setActionNextSchedule(RESET);
				currentModalId.current = null;
			};

			if (actionNextSchedule < now) {
				checkNotificationsStatus({ ...props, onDismiss });
			}
		},
		[actionNextSchedule, setActionNextSchedule, checkNotificationsStatus, isPushNotificationsEnabled]
	);

	useSubscribe(EventStore.ON_LOGOUT, unRegisterMessaging);

	// Setup Initial Schedule
	useEffect(() => {
		if (!isPushNotificationsEnabled) return;
		if (permission === PermissionStatusEnum.DEFAULT && initialSchedule === null && !areSettingsLoading) {
			const nextSchedule = new DateObject().add(pushNotificationsPromptMinutesDelay, 'minutes').unix();

			setInitialSchedule(nextSchedule);
		}
	}, [
		permission,
		initialSchedule,
		setInitialSchedule,
		pushNotificationsPromptMinutesDelay,
		areSettingsLoading,
		isPushNotificationsEnabled
	]);

	// Setup Schedule After Actions
	useEffect(() => {
		if (!isPushNotificationsEnabled) return;
		if (permission === PermissionStatusEnum.DEFAULT && actionNextSchedule === null && !areSettingsLoading) {
			const nextSchedule = new DateObject().add(pushNotificationsActionsPromptMinutesDelay, 'minutes').unix();

			setActionNextSchedule(nextSchedule);
		}
	}, [
		permission,
		actionNextSchedule,
		setActionNextSchedule,
		pushNotificationsActionsPromptMinutesDelay,
		areSettingsLoading,
		isPushNotificationsEnabled
	]);

	// Register Service Worker
	useEffect(() => {
		registerServiceWorker();
	}, [registerServiceWorker]);

	// Setup Firebase
	useEffect(() => {
		if (!isPushNotificationsEnabled) return;
		if (isAuthenticated && swRegistration && permission === PermissionStatusEnum.GRANTED) {
			setupFirebase();
		}
	}, [swRegistration, isAuthenticated, setupFirebase, permission, isPushNotificationsEnabled]);

	const value: WebPushContextShape = useMemo(
		() => ({
			canRequestPermission: permission === PermissionStatusEnum.DEFAULT && isSupported,
			displayEnablePushNotificationsBySettingsModal,
			isPermissionDenied: permission === PermissionStatusEnum.DENIED,
			isPermissionGranted: permission === PermissionStatusEnum.GRANTED,
			isSupported: isSupported,
			requestInitialPermission,
			requestNotificationsPermission,
			requestPermission: checkNotificationsStatus,
			requestPermissionAfterAction
		}),
		[
			checkNotificationsStatus,
			permission,
			isSupported,
			requestInitialPermission,
			requestPermissionAfterAction,
			displayEnablePushNotificationsBySettingsModal,
			requestNotificationsPermission
		]
	);

	return <WebPushContext.Provider value={value}>{children}</WebPushContext.Provider>;
};
